import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Scenario: Fatima, 56 — Omega-3 & Heart Health (FADS1/FADS2)

export const omega3Fatima: Scenario = {
  slug: 'omega3-fatima',
  series: 'Cardiovascular Risk',
  title: "She ate for her heart. Her triglycerides still climbed.",
  socialHook:
    "Fatima, 56: fish twice a week, olive oil, almost no red meat — and her triglycerides still climbed. The missing piece was the type of omega-3 her body could actually use.",
  meta: {
    description:
      "A fictional case study: Fatima followed heart-healthy advice for five years, but her triglycerides stayed high and her omega-3 index stayed low. Her FADS1/FADS2 results explain why conversion, not effort, was the issue.",
  },

  persona: {
    name: 'Fatima',
    age: 56,
    sex: 'Female',
    ancestry: 'North African / Moroccan',
    occupation: 'Retired teacher',
    chiefConcern:
      "Her triglycerides have stayed high for five years despite fish, olive oil, vegetables, and avoiding red meat. Her cardiologist is considering medication, and she wants to know why the diet did not work.",
    familyHistory: 'Father: myocardial infarction at age 67.',
  },

  clinicalPicture: {
    symptoms: [
      'No chest pain or exertional symptoms',
      'Mild fatigue, especially in the afternoons',
      'Occasional brain fog — she attributes it to retirement adjustment',
    ],
    labs: [
      { name: 'Triglycerides', value: '120 mg/dL', refRange: '<66 mg/dL', flag: 'high' },
      { name: 'Omega-3 Index', value: '4.2%', refRange: '>8% (optimal)', flag: 'low' },
    ],
    medications: ['No prescription medications (cardiologist considering fibrates or statins)'],
    supplements: ['Occasional fish oil — low dose, irregular'],
    lifestyle: [
      'Diet: salmon twice a week, olive oil exclusively, minimal red meat, plenty of vegetables',
      'Non-smoker',
      'Alcohol: 1–2 glasses of wine per week',
      'Moderate activity — daily walks, no structured exercise',
    ],
  },

  genetics: [
    {
      gene: 'FADS1',
      variant: 'Reduced-function variant',
      rsid: 'rs174537',
      genotype: 'Reduced-function allele',
      plainMeaning:
        "Fatima may eat omega-3 foods, but her body is poor at turning some of them into the EPA form her heart needs.",
      technicalDetail:
        'FADS1 encodes the enzyme delta-5-desaturase, which performs the final step in converting short-chain omega-3 (ALA, found in plants and some fish) into EPA — the long-chain form the cardiovascular system can actually use. Reduced-function variants severely impair this conversion, cutting it from an already-modest ~15% average down to as low as 5–10%.',
    },
    {
      gene: 'FADS2',
      variant: 'Reduced-function variant',
      rsid: 'rs1535',
      genotype: 'Reduced-function allele',
      plainMeaning:
        "This result slows the start of the same conversion chain, so the whole pathway delivers less usable EPA and DHA.",
      technicalDetail:
        "FADS2 encodes delta-6-desaturase, the enzyme that starts the omega-3 conversion chain before FADS1 picks it up. Reduced-function variants at both FADS1 and FADS2 together mean the entire conversion pathway is impaired. The omega-3 Fatima eats — whether from plants, flaxseed oil, or most fish — is almost entirely failing to reach her cells as usable EPA or DHA.",
    },
    {
      gene: 'APOE',
      variant: 'ε3/ε4',
      rsid: 'rs429358 / rs7412',
      genotype: 'One copy of ε4',
      plainMeaning:
        "This result can make dietary fats harder to clear, which makes getting the right omega-3 form more important for her.",
      technicalDetail:
        "APOE ε3/ε4 affects how dietary fats are transported and cleared from the bloodstream. ε4 carriers tend to have a stronger triglyceride response to dietary fat and impaired clearance — compounding the effect of poor omega-3 status and making it especially important to get EPA and DHA levels right.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'Five years of doing what she was told',
      body: "Fatima changed her diet after her father's heart attack. Salmon twice a week. Olive oil instead of butter. Vegetables at every meal. Almost no red meat. Her cardiologist told her to keep going. Five years later, her triglycerides are still high, and her omega-3 index is low. Now medication is on the table. She is frustrated because she did what she was told, and the numbers did not move.",
    },
    {
      type: 'narrative',
      heading: "The omega-3 clue",
      body: "The mystery is that not all omega-3 acts the same way. Plant omega-3s and some food sources need to be converted into EPA and DHA, the long-chain forms most tied to triglycerides and heart health. That conversion is limited even in many healthy people. Fatima's omega-3 index shows the result directly: despite years of omega-3 foods, her EPA and DHA level is still low.",
    },
    {
      type: 'reveal',
      heading: 'Her body was not converting enough',
      body: "The issue was not discipline. It was conversion. Her FADS1 and FADS2 results sit at the start and end of the omega-3 conversion chain, so less short-chain omega-3 becomes usable EPA and DHA. Five years of a heart-healthy diet helped in many ways, but it did not deliver enough of the specific forms her labs show she needs. For Fatima, pre-formed EPA and DHA matter more than simply eating more ALA-rich foods.",
    },
    {
      type: 'reveal',
      heading: 'One more reason the pattern stuck',
      body: "Her APOE result adds another layer. One copy of ε4 can make dietary fats harder to transport and clear. It is not the main driver of this story, but it makes the FADS finding more important: she is converting less omega-3 into usable forms while also having a lipid system that may be harder to shift. The lab pattern now has a reason.",
    },
    {
      type: 'action',
      heading: "Five changes to discuss with her clinician",
      items: [
        "Ask about a concentrated EPA and DHA supplement, often 2–4g per day combined when clinically appropriate. Fish oil and algae oil both bypass the conversion step.",
        "Choose fish higher in pre-formed EPA and DHA, such as sardines, mackerel, and wild salmon. The type of fish matters as much as the number of servings.",
        "Recheck the omega-3 index in about 3 months. It measures EPA and DHA in red blood cell membranes and can show whether the plan is working.",
        "Reduce refined carbohydrates. White bread, rice, pastries, and sugary foods can raise triglycerides through carbohydrate metabolism, separate from omega-3 status.",
        "Ask the cardiologist whether a focused 3-month trial is reasonable before long-term medication. Rechecking triglycerides and omega-3 index together would make the next decision clearer.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "What her omega-3 status showed",
      caption:
        "Five years of the wrong omega-3 form did not move the cardiovascular picture enough. These changes target the form her body can use.",
      baselineLabel: "Current picture after 5 years \"eating right\"",
      baseline: 78,
      levers: [
        {
          label: 'Switch to EPA/DHA supplement 2-4g/day',
          delta: -18,
          description:
            'Bypasses the FADS conversion step and delivers pre-formed EPA and DHA directly to the cardiovascular system.',
        },
        {
          label: 'Switch fish sources (sardines, mackerel)',
          delta: -8,
          description:
            'Higher-EPA fish replace lower-EPA choices and build more direct EPA intake.',
        },
        {
          label: 'Reduce refined carbohydrates',
          delta: -12,
          description:
            'Refined carbs are a primary triglyceride driver independent of omega-3 status. Reducing them addresses the other half of her elevated triglyceride picture.',
        },
        {
          label: 'Recheck omega-3 index to guide dosing',
          delta: -5,
          description:
            'Monitoring EPA and DHA in red blood cell membranes confirms whether the supplement is working and guides dose adjustments.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'omega3-fatima',
    label: 'Explore your omega-3 genetics in Coach',
  },
}
